import React, { useState, useEffect } from 'react';
import Sheet from '../common/Sheet';

// Bottom sheet for every tier BELOW desktop (all mobile + all tablet, < 1280) — desktop (>= 1280) is the
// only tier that opts out to the native <select>. (util/layouts TABLET_SHEET_MQ covers just the 1024–1279
// tablet band; mobile is < 1024 — this one query spans both, matching the app's sheet-below-1280 rule.)
const BELOW_DESKTOP_MQ = '(max-width: 1279px)';
const matchBelowDesktop = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia(BELOW_DESKTOP_MQ).matches;

/**
 * SheetSelect — a single-select that renders as a native <select> on wider layouts, and as a
 * tap-to-open bottom SHEET (thumb zone) on mobile-portrait base. The one place setup dropdowns become
 * the app's bottom-sheet pattern (David 2026-08-20 — camera / event / scale-indicator pickers). Models
 * the existing mcplayback/CameraPicker listbox (option rows + a ✓ on the active one). REUSES common/Sheet.
 *
 * The mobile/desktop split is a BEHAVIOUR gate (which control), not a style breakpoint, so it uses a
 * matchMedia check (like isSetupMobileLandscape) rather than CSS — CSS can't swap a native picker for a
 * sheet. matchMedia is absent under jsdom → tests get the native <select> (existing combobox assertions
 * keep passing); the sheet variant is covered by matchMedia-mocked tests.
 *
 * Props:
 *   value      — current value (compared as String)
 *   options    — [{ value, label, disabled? }]
 *   onChange   — (nextValue) => void   (nextValue is always a String, matching the native select)
 *   disabled   — whole control disabled
 *   className  — applied to BOTH the native select and the sheet trigger (keeps size/alignment identical)
 *   title      — sheet heading + trigger aria-label (e.g. "Camera", "Scale Indicator Type")
 *   placeholder— trigger text when nothing matches (defaults to the first option's label or "Select…")
 */
const SheetSelect = ({ value, options = [], onChange, disabled, className = '', title, placeholder }) => {
  const [open, setOpen] = useState(false);
  // The bottom sheet is the BASE for every tier BELOW desktop — all mobile AND all tablet (< 1280).
  // Desktop (>= 1280) is the only tier that opts out to the native <select>. Mobile-first, bubbling
  // upward through tablet — not a narrow mobile-portrait exception.
  const [sheet, setSheet] = useState(matchBelowDesktop);
  const val = value != null ? String(value) : '';

  // Track the tier live so a resize/rotation flips native<->sheet AND dismisses an open sheet.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(BELOW_DESKTOP_MQ);
    const onChange = () => { setSheet(mql.matches); setOpen(false); };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  // Desktop only (>= 1280): the ordinary native select (unchanged behaviour + a11y).
  if (!sheet) {
    return (
      <select className={className} value={val} disabled={disabled} aria-label={title}
        onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)} disabled={o.disabled}>{o.label}</option>
        ))}
      </select>
    );
  }

  // Base mobile: a trigger showing the current label, opening a bottom sheet of options.
  const cur = options.find((o) => String(o.value) === val);
  const triggerText = cur ? cur.label : (placeholder || (options[0] && options[0].label) || 'Select…');
  return (
    <>
      <button type="button" className={className + ' setup-sheet-select__trigger'} disabled={disabled}
        aria-haspopup="listbox" aria-label={title} onClick={() => setOpen(true)}>
        <span className="setup-sheet-select__value">{triggerText}</span>
        <span className="setup-sheet-select__caret" aria-hidden="true">▾</span>
      </button>
      {open ? (
        <Sheet className="app-sheet" title={title} onClose={() => setOpen(false)}>
          <div className="setup-sheet-select__list" role="listbox" aria-label={title}>
            {options.map((o) => {
              const active = String(o.value) === val;
              return (
                <button key={String(o.value)} type="button" role="option" aria-selected={active}
                  disabled={o.disabled}
                  className={'setup-sheet-select__opt' + (active ? ' setup-sheet-select__opt--active' : '') + (o.disabled ? ' setup-sheet-select__opt--disabled' : '')}
                  onClick={() => { if (!o.disabled) { onChange(String(o.value)); setOpen(false); } }}>
                  <span className="setup-sheet-select__opt-label">{o.label}</span>
                  {active ? <span className="setup-sheet-select__opt-check" aria-hidden="true">✓</span> : null}
                </button>
              );
            })}
          </div>
        </Sheet>
      ) : null}
    </>
  );
};

export default SheetSelect;
